import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import ReactQuill from 'react-quill';
import { Upload } from 'antd';
import {
  convertTime,
  emailValidationRegExp,
  filenameToExtension,
  getServiceEntity,
  getZ3PConfigValue,
} from '../../helper';
import { carMakeOptions } from '../../VehicleConstants';
import { usStatesWithoutAbbreviation } from '../../UsStates';
import 'react-quill/dist/quill.snow.css';
import UploadPhotoIcon from '@material-ui/icons/AddPhotoAlternateOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress, IconButton } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import { getBrain2Url, getProNameHeader } from '../../brainApi';

const useStyles = makeStyles((theme) => ({
  logoActionIcon: {
    fontSize: '24px',
    cursor: 'pointer',
  },
  logoImagePreview: {
    float: 'left',
    height: '100px',
  },
  logoImageToolbar: {
    float: 'left',
    marginLeft: '10px',
  },
}));

const ClientProfileSettings = (props) => {
  const {
    setting,
    clientConfigurationSettings,
    setClientConfigurationSettings,
    updatedConfiguration,
    setUpdatedConfiguration,
    errors,
    setErrors,
    showErrors,
    setShowErrors,
    showDetailingPriceSheetInput,
  } = props;
  const translate = useTranslate();
  const notify = useNotify();
  const styles = useStyles();
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const handleConfigurationUpdate = (key, value) => {
    // On each update, save to updated configuration to only send changed values to DB
    setUpdatedConfiguration((prevState) => ({
      ...prevState,
      [key]: value,
    }));
    // On each update, save to current configuration to render correct values on admin page
    const currentClientConfiguration = clientConfigurationSettings[key];
    currentClientConfiguration.value = value;
    setClientConfigurationSettings((prevState) => ({
      ...prevState,
      [key]: currentClientConfiguration,
    }));
  };

  const handleInputChange = (event, isBoolean = false) => {
    if (showErrors) {
      setShowErrors(false);
    }
    const key = Object.keys(setting)[0];
    if (isBoolean) {
      handleConfigurationUpdate(key, !clientConfigurationSettings[key].value);
    } else if (key === 'email_welcome_message') {
      // eslint-disable-next-line no-unused-expressions
      event === ''
        ? handleConfigurationUpdate(key, null)
        : handleConfigurationUpdate(key, event);
    } else if (event.target.value === '') {
      handleConfigurationUpdate(key, null);
    } else {
      handleConfigurationUpdate(key, event.target.value);
    }
  };

  const validateMinimumOnsiteHours = (val) => {
    const timeRangeMax = convertTime(
      clientConfigurationSettings.time_range_max.value,
      'militaryNumber',
    );
    const timeRangeMin = convertTime(
      clientConfigurationSettings.time_range_min.value,
      'militaryNumber',
    );
    const timeRangeDifference = timeRangeMax - timeRangeMin;
    return val <= timeRangeDifference;
  };

  const formValidation = (
    regexp,
    key,
    value,
    multiple = false,
    customValidation = () => {},
  ) => {
    let hasRegexErrors = value ? !regexp.test(value) : false;
    if (multiple && value && value.includes(',')) {
      const entries = value.replace(/\s/g, '').split(',');
      // If any entry is true, return true to show the error
      hasRegexErrors = entries.some((entry) =>
        entry ? !regexp.test(entry) : false,
      );
    }

    let hasCustomValidationErrors = false;
    if (value) {
      hasCustomValidationErrors =
        customValidation(value) !== undefined && !customValidation(value);
    }

    if (
      (hasRegexErrors || hasCustomValidationErrors) &&
      !errors.includes(key)
    ) {
      setErrors([...errors, key]);
    } else if (
      !hasRegexErrors &&
      !hasCustomValidationErrors &&
      errors.includes(key)
    ) {
      const index = errors.indexOf(key);
      const newErrors = errors;
      newErrors.splice(index, 1);
      setErrors(newErrors);
    }
    return hasRegexErrors || hasCustomValidationErrors;
  };

  const selectInputField = (key, value, label, options, multiple = false) => {
    let defaultValue;
    if (multiple) {
      defaultValue = options
        .filter((option) => value && value.includes(option.name))
        .map((item) => item.name);
    } else {
      const defaultOption = options.find((option) => option.name === value);
      defaultValue = defaultOption ? defaultOption.name : '';
    }
    return (
      <div>
        <InputLabel id={key} shrink>
          {label}
        </InputLabel>
        <Select
          id={key}
          multiple={multiple}
          value={defaultValue}
          onChange={(event) => handleInputChange(event)}
          margin="dense"
          fullWidth
        >
          {options.map((option) => {
            return (
              <MenuItem key={option.id} value={option.name}>
                {option.name}
              </MenuItem>
            );
          })}
        </Select>
      </div>
    );
  };

  const inputField = () => {
    const key = Object.keys(setting)[0];
    const { value, data_type: dataType } = clientConfigurationSettings[key];
    const label = translate(`resources.business-configuration.fields.${key}`);

    const schedulingSystem = getZ3PConfigValue('scheduling_system');
    if (
      schedulingSystem === 'availability_windows' &&
      key === 'lead_time_time_slots_number_of_hours'
    ) {
      return null;
    }
    if (
      schedulingSystem === 'time_slots' &&
      [
        'lead_time_availability_windows_time_of_day',
        'lead_time_availability_windows_number_of_days',
      ].includes(key)
    ) {
      return null;
    }

    if (key === 'lead_time_availability_windows_time_of_day') {
      const helperText = 'All times should end in :00 or :30';
      const regexp = new RegExp(':00|:30');
      return (
        <>
          <InputLabel id={key} shrink>
            {label}
          </InputLabel>
          <TextField
            id={key}
            type="time"
            value={value === '' ? '' : convertTime(value, 'militaryString')}
            onChange={(event) => handleInputChange(event)}
            error={
              typeof value === 'string' && value !== ''
                ? formValidation(regexp, key, value)
                : false
            }
            helperText={
              typeof value === 'string' && value !== '' && !regexp.test(value)
                ? helperText
                : false
            }
            margin="dense"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => {
                    handleConfigurationUpdate(
                      'lead_time_availability_windows_time_of_day',
                      '',
                    );
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </>
      );
    }
    if (key === 'lead_time_availability_windows_number_of_days') {
      const lead_time_time_of_day =
        clientConfigurationSettings.lead_time_availability_windows_time_of_day
          .value;
      return (
        <>
          <InputLabel id={key} shrink>
            {label}
          </InputLabel>
          <Select
            id={key}
            value={
              !lead_time_time_of_day || lead_time_time_of_day === ''
                ? null
                : value
            }
            onChange={(event) => handleInputChange(event)}
            margin="dense"
            disabled={!lead_time_time_of_day || lead_time_time_of_day === ''}
          >
            <MenuItem value={0}>The day of</MenuItem>
            <MenuItem value={1}>The day before</MenuItem>
            <MenuItem value={2}>2 days before</MenuItem>
          </Select>
        </>
      );
    }
    if (key === 'lead_time_time_slots_number_of_hours') {
      return (
        <TextField
          id={key}
          type="number"
          label={label}
          value={value}
          onChange={(event) => handleInputChange(event)}
          margin="dense"
          fullWidth
        />
      );
    }
    if (key === 'email_welcome_message') {
      const quillToolbarOptions = {
        toolbar: [
          ['bold', 'italic', 'underline', 'strike'], // toggled buttons
          ['link', 'image'],
          ['blockquote'],

          [{ header: 1 }, { header: 2 }], // custom button values
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
          [{ direction: 'rtl' }], // text direction

          [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
          [{ header: [1, 2, 3, 4, 5, 6, false] }],

          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ align: [] }],

          ['clean'], // remove formatting button
        ],
      };
      return (
        <div>
          <br />
          <div style={{ fontSize: '.7rem' }}>
            {label}
            <ReactQuill
              theme="snow"
              modules={quillToolbarOptions}
              defaultValue={value}
              onChange={(data) => handleInputChange(data)}
            />
          </div>
        </div>
      );
    }
    if (key === 'calendar_color') {
      return (
        <div>
          <InputLabel id="calendar-color-label" shrink>
            {label}
          </InputLabel>
          <Select
            id={key}
            value={value}
            onChange={(event) => handleInputChange(event)}
            margin="dense"
          >
            <MenuItem id={key} value="fullness">
              Fullness (Availability)
            </MenuItem>
            <MenuItem id={key} value="serviceVehicle">
              {getServiceEntity()}
            </MenuItem>
          </Select>
        </div>
      );
    }

    if (key === 'detailing_price_sheet') {
      return showDetailingPriceSheetInput ? (
        <div>
          <InputLabel id="detailing-price-sheet-label" shrink>
            {label}
          </InputLabel>
          <Select
            id={key}
            value={value}
            displayEmpty
            onChange={(event) => handleInputChange(event)}
            margin="dense"
          >
            <MenuItem value={null}>None</MenuItem>
            <MenuItem id={key} value={3}>
              3-Vehicle Price Sheets
            </MenuItem>
            <MenuItem id={key} value={7}>
              7-Vehicle Price Sheets
            </MenuItem>
          </Select>
        </div>
      ) : (
        <span style={{ verticalAlign: 'super' }}>
          You are configured with the {value}-Vehicle Price Sheet setup. To make
          changes, please reach out to the Zippity team.
        </span>
      );
    }

    const vehicleTabsTolabels = {
      license: 'License Plate',
      year: 'Year Make Model',
      vin: 'VIN',
      boat: 'Boat',
      atv: 'ATV',
      rv: 'RV',
      motorcycle: 'Motorcycle',
      plane: 'Plane',
      heavy: 'Heavy Duty',
    };
    const isUsingHeavyDuty = getZ3PConfigValue('is_using_heavy_duty');

    if (key === 'available_vehicle_entry_tabs') {
      const regexp = new RegExp(/.+/); // accepts any non-blank value
      let helperText = 'Must select at least one form of vehicle entry';
      const validateAvailableTabs = (val) => {
        if (!isUsingHeavyDuty && val.includes('heavy')) {
          helperText = 'Heavy Duty is not supported for this Pro';
          return false;
        }
        return true;
      };
      const isInvalid = formValidation(
        regexp,
        key,
        value,
        false,
        validateAvailableTabs,
      );
      return (
        <div>
          <InputLabel id="vehicle-tab-label" shrink>
            {label}
          </InputLabel>
          <Select
            id={key}
            value={value}
            multiple
            onChange={(event) => handleInputChange(event)}
            margin="dense"
            error={isInvalid}
          >
            {Object.entries(vehicleTabsTolabels).map(([tab, tabLabel]) => (
              <MenuItem id={tab} key={tab} value={tab}>
                {tabLabel}
              </MenuItem>
            ))}
          </Select>
          {isInvalid && <FormHelperText error>{helperText}</FormHelperText>}
        </div>
      );
    }

    if (key === 'default_vehicle_entry_tab') {
      const regexp = new RegExp(/.+/); // accepts any non-blank value
      const helperText =
        'Default tab must be included in Available Vehicle Entry Tabs';
      const validateDefaultTab = (val) => {
        const availableTabs =
          updatedConfiguration.available_vehicle_entry_tabs ||
          clientConfigurationSettings.available_vehicle_entry_tabs.value;
        return availableTabs.includes(val);
      };
      const isInvalid = formValidation(
        regexp,
        key,
        value,
        false,
        validateDefaultTab,
      );
      return (
        <div>
          <InputLabel id="vehicle-tab-label" shrink>
            {label}
          </InputLabel>
          <Select
            id={key}
            value={value}
            onChange={(event) => handleInputChange(event)}
            margin="dense"
            error={isInvalid}
          >
            {Object.entries(vehicleTabsTolabels).map(([tab, tabLabel]) => (
              <MenuItem id={tab} key={tab} value={tab}>
                {tabLabel}
              </MenuItem>
            ))}
          </Select>
          {isInvalid && <FormHelperText error>{helperText}</FormHelperText>}
        </div>
      );
    }

    if (key === 'booking_flow_css_file') {
      return (
        <div>
          <InputLabel id="booking_flow_css_file-label" shrink>
            {label}
          </InputLabel>
          <Select
            id={key}
            value={value}
            onChange={(event) => handleInputChange(event)}
            margin="dense"
          >
            <MenuItem id={key} value="zippity-modern.css">
              Blue - Modern
            </MenuItem>
            <MenuItem id={key} value="zippity-blue.css">
              Blue - Old
            </MenuItem>
            <MenuItem id={key} value="zippity-red.css">
              Red
            </MenuItem>
            <MenuItem id={key} value="zippity-aaa.css">
              AAA
            </MenuItem>
            <MenuItem id={key} value="zippity.css">
              Teal
            </MenuItem>
          </Select>
        </div>
      );
    }

    const signingUrl = `${getBrain2Url()}/util/s3-sign/booking-app`;

    const handleImage = (key, val) => {
      if (!val) {
        if (value) {
          // User clicked the delete button to remove the image they uploaded.
          handleConfigurationUpdate(key, null);
        } else {
          // User tried to upload a file that's not an allowed image type.
          notify('Error: File type not allowed', 'warning');
        }
        return;
      }

      setIsUploadingImage(true);

      const extension = filenameToExtension(val.name);
      const currentTimestamp = Date.now();
      const filename = `${currentTimestamp}-${val.name}`;

      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Pro-Name': getProNameHeader(),
      };

      // Step 1: Call Brain2 to get a signed S3 URL
      fetch(`${signingUrl}?objectName=${filename}&contentType=image/*`, {
        headers,
      })
        .then((res) => res.json())
        .then((res) => {
          const { signedUrl } = res;
          if (signedUrl) {
            // Step 2: Use the signed URL to upload the image to S3
            fetch(signedUrl, {
              method: 'PUT',
              headers: { 'Content-Type': `image/${extension}` },
              body: val,
            }).then((data) => {
              setIsUploadingImage(false);
              if (data.status === 200) {
                // Step 3: Use the S3 image url to save logo into z3p_configuration
                const url = `https://zippityinfra.imgix.net/booking-app/${filename}?auto=format,compress`;
                handleConfigurationUpdate(key, url);
              } else {
                console.error(data);
                notify('Error: Could not upload file', 'warning');
              }
            });
          } else {
            setIsUploadingImage(false);
            console.error(res);
            notify('Error: Could not upload file', 'warning');
          }
        })
        .catch((err) => {
          setIsUploadingImage(false);
          console.error(err);
          notify('Error: Could not upload file', 'warning');
        });
    };

    if (key === 'logo' || dataType === 'imageUrl') {
      const uploadProps = {
        name: 'image',
        accept: 'image/*',
        fileList: [],
        maxCount: 1,
        beforeUpload(file) {
          handleImage(key, file);
        },
        // dummy request to bypass making a POST request
        customRequest({ onSuccess }) {
          setTimeout(() => {
            onSuccess('ok');
          }, 0);
        },
      };

      return (
        <div>
          <InputLabel id={key} shrink>
            {label}
          </InputLabel>

          {isUploadingImage && <CircularProgress />}

          {!!value && <img src={value} className={styles.logoImagePreview} />}

          <div className={styles.logoImageToolbar}>
            {!!value && (
              <DeleteIcon
                className={styles.logoActionIcon}
                onClick={() => handleImage(key, null)}
              />
            )}
            <Upload {...uploadProps}>
              <UploadPhotoIcon className={styles.logoActionIcon} />
            </Upload>
          </div>
        </div>
      );
    }
    if (
      [
        'default_arrival_time',
        'default_departure_time',
        'time_range_min',
        'time_range_max',
      ].includes(key)
    ) {
      const helperText = 'All times should end in :00 or :30';
      const regexp = new RegExp(':00|:30');
      return (
        <div>
          <InputLabel id={key} shrink>
            {label}
          </InputLabel>
          <TextField
            id="time"
            type="time"
            value={convertTime(value, 'militaryString')}
            onChange={(event) => handleInputChange(event)}
            error={
              typeof value === 'string'
                ? formValidation(regexp, key, value)
                : false
            }
            helperText={
              typeof value === 'string' && !regexp.test(value)
                ? helperText
                : false
            }
            margin="dense"
            fullwidth
          />
        </div>
      );
    }
    if (key === 'time_range_marks') {
      let valueToDisplay = value ? Object.keys(value).join(',') : '';
      if (typeof value === 'string') {
        valueToDisplay = value;
      }
      const regexp = new RegExp('^[0-9,. ]*$');
      return (
        <TextField
          id={key}
          type="text"
          label={label}
          value={valueToDisplay}
          onChange={(event) => handleInputChange(event)}
          error={formValidation(regexp, key, valueToDisplay)}
          helperText={
            formValidation(regexp, key, valueToDisplay)
              ? 'Please only enter numbers (military time), separated by commas'
              : 'This is a comma-separated list of the time marks you want on the slider. Enter in military time (ex: 14.5 if you want 2:30PM)'
          }
          margin="dense"
          fullWidth
        />
      );
    }
    if (dataType === 'boolean') {
      return (
        <FormControlLabel
          label={label}
          control={
            <Switch
              id={key}
              checked={value}
              onChange={(event) => handleInputChange(event, true)}
              margin="dense"
              fullWidth
            />
          }
        />
      );
    }
    if (
      ['recall_makes_offered', 'at_home_recall_ids_offered'].includes(key) &&
      clientConfigurationSettings.is_performing_recalls &&
      !clientConfigurationSettings.is_performing_recalls.value
    ) {
      // eslint-disable-next-line consistent-return
      return;
    }
    if (key === 'default_state') {
      return selectInputField(key, value, label, usStatesWithoutAbbreviation);
    }
    if (key === 'recall_makes_offered') {
      return selectInputField(key, value, label, carMakeOptions, true);
    }
    if (dataType === 'array') {
      const valueToDisplay = Array.isArray(value) ? value.join(',') : value;
      return (
        <TextField
          id={key}
          type="text"
          label={label}
          value={valueToDisplay}
          onChange={(event) => handleInputChange(event)}
          helperText="This is a comma-separated list (ex: item1,item2)"
          margin="dense"
          fullWidth
        />
      );
    }
    const errorObject = {
      call_forwarding_phone_raw: {
        regexp: new RegExp('^(\\+1)?[0-9]{10}$'),
        helperText:
          'This phone number must be exactly 10 digits, without any special characters',
        multiple: false,
      },
      customer_facing_phone_raw: {
        regexp: new RegExp('^(\\+1)?[0-9]{10}$'),
        helperText:
          'This phone number must be exactly 10 digits, without any special characters',
        multiple: false,
      },
      client_url: {
        regexp: new RegExp('^http'),
        helperText: 'This URL must begin with http',
        multiple: false,
      },
      customer_facing_email: {
        regexp: emailValidationRegExp(),
        helperText: 'You must enter a valid email address',
        multiple: false,
      },
      support_email: {
        regexp: emailValidationRegExp(),
        helperText: 'You must enter a valid email address',
        multiple: false,
      },
      terms_and_conditions_url: {
        regexp: new RegExp('^http'),
        helperText: 'This URL must begin with http',
        multiple: false,
      },
      bcc_email_address: {
        regexp: emailValidationRegExp(),
        helperText: 'You must enter a valid email address',
        multiple: true,
      },
      minimum_onsite_hours: {
        // RegExp for 0-24 including up to 2 decimal places
        regexp: new RegExp(
          '^((?:[0-9]|1[0-9]|2[0-3])(?:\\.\\d{1,2})?|24(?:\\.00?)?)$',
        ),
        helperText: 'Minimum onsite hours cannot exceed scheduling time range',
        multiple: false,
        customValidation: validateMinimumOnsiteHours,
      },
    };
    return (
      <TextField
        id={key}
        type={dataType === 'integer' ? 'number' : 'text'}
        onWheel={dataType === "integer" ? (e) => e.target.blur() : undefined}
        label={label}
        value={value}
        disabled={key === 'my_simple_shop_url' ? true : false}
        onChange={(event) => handleInputChange(event)}
        error={
          Object.keys(errorObject).includes(key)
            ? formValidation(
                errorObject[key].regexp,
                key,
                value,
                errorObject[key].multiple,
                errorObject[key]?.customValidation,
              )
            : false
        }
        helperText={
          Object.keys(errorObject).includes(key) &&
          value &&
          formValidation(
            errorObject[key].regexp,
            key,
            value,
            errorObject[key].multiple,
            errorObject[key]?.customValidation,
          )
            ? errorObject[key].helperText
            : false
        }
        margin="dense"
        fullWidth
      />
    );
  };

  return <div>{inputField()}</div>;
};

export default ClientProfileSettings;
